import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import ImageUpload from '../components/ImageUpload';

const API_URL = '/api/categories';

const Kategoriyalar = () => {
  const { user } = useAuth();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // State management
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal and form state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit'
  const [form, setForm] = useState({ name: '', image: null });
  const [editId, setEditId] = useState(null);

  // Confirm delete modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Search state
  const [search, setSearch] = useState('');

  // Fetch data function
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
      });
      if (search.trim() !== '') params.append('search', search.trim());
      const response = await fetch(
        `/api/categories/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Owner-Id': String(user?.ownerId || user?.id || ''),
            'X-Staff-Id': String(user?.staffId ?? '')
          }
        }
      );
      const data = await response.json();
      setCategories(data.items);
      setTotalCount(data.total);
    } catch (err) {
      setError('Failed to fetch categories');
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page if search changes
  }, [search, itemsPerPage]);

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, search]);

  // Handlers
  const handleOpenAdd = () => {
    setModalType('add');
    setForm({ name: '', image: null });
    setShowModal(true);
    setEditId(null);
  };

  const handleOpenEdit = (category) => {
    setModalType('edit');
    setForm({ name: category.name, image: category.image || null });
    setEditId(category.id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setForm({ name: '', image: null });
    setEditId(null);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = modalType === 'add' ? API_URL : `${API_URL}/${editId}`;
      const method = modalType === 'add' ? 'POST' : 'PUT';
      
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Owner-Id': String(user?.ownerId || user?.id || ''),
          'X-Staff-Id': String(user?.staffId ?? '')
        },
        body: JSON.stringify({
          ...form,
          owner_id: user.role === 'owner' ? user.id : user.owner_id
        })
      });

      if (!response.ok) throw new Error('Operation failed');
      
      const responseData = await response.json();
      
      // Manually update state after successful operation
      if (modalType === 'add') {
        setCategories(prev => [responseData, ...prev]);
      } else {
        setCategories(prev => prev.map(cat => 
          cat.id === editId ? {...cat, ...form} : cat
        ));
      }
      
      toast.success(`Category ${modalType === 'add' ? 'added' : 'updated'} successfully`);
      handleCloseModal();
      
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(`${API_URL}/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Owner-Id': String(user?.ownerId || user?.id || ''),
          'X-Staff-Id': String(user?.staffId ?? '')
        }
      });

      if (!response.ok) throw new Error('Delete failed');
      
      // Manually update state after successful delete
      setCategories(prev => prev.filter(cat => cat.id !== deleteId));
      
      toast.success('Category deleted successfully');
      setShowConfirm(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Render
  return (
    <div className="container mt-4">
      <div className='d-flex justify-content-between mb-4' >
        <h1>Kategoriyalar</h1>
        <input
          type="text"
          className="form-control w-25"
          style={{ maxWidth: 300 }}
          placeholder="Kategoriya nomi bo'yicha qidiruv"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn btn-success mb-3" onClick={handleOpenAdd}>+ Kategoriya qo'shish</button>
      </div>
     {loading ? (
        <Loader />
      ) : error ? (
        <div className="alert alert-danger text-center">{error}</div>
      ) : categories && categories.length > 0 ? (
        <>
          <div className="table-responsive rounded-4 shadow">
            <table className="table table-bordered mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Rasm</th>
                <th>Nomi</th>
                <th>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, idx) => (
                <tr key={cat.id}>
                  <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                  <td style={{ width: 80 }}>
                    {cat.image ? (
                      <img src={cat.image} alt={cat.name} className="img-thumbnail" style={{ width: 60, height: 60, objectFit: 'cover' }} />
                    ) : (
                      <span className="text-muted">Yo'q</span>
                    )}
                  </td>
                  <td>{cat.name}</td>
                  <td>
                    <button className="btn btn-sm btn-warning me-2" onClick={() => handleOpenEdit(cat)} title="Tahrirlash">
                      <FaEdit />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => { setShowConfirm(true); setDeleteId(cat.id); }} title="O'chirish">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
        </>
      ) : (
        <div className="alert alert-info text-center">Ma'lumot topilmadi</div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">{modalType === 'add' ? "Kategoriya qo'shish" : 'Kategoriyani tahrirlash'}</h5>
                  <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nomi</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <ImageUpload
                      label="Kategoriya rasmi (300x300)"
                      initialImage={form.image ? (form.image.startsWith('/') ? form.image : `/${form.image}`) : null}
                      onUpload={(path) => setForm(prev => ({ ...prev, image: path }))}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Bekor qilish</button>
                  <button type="submit" className="btn btn-success">Saqlash</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        show={showConfirm}
        onConfirm={handleDelete}
        onCancel={() => { setShowConfirm(false); setDeleteId(null); }}
        message="Ushbu kategoriyani o'chirishni tasdiqlaysizmi?"
      />
    </div>
  );
};

export default Kategoriyalar; 